import React, { useEffect, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogTitle
} from '@/components/ui/dialog';
import {
  Radar, Sparkles, Target, ExternalLink, Image as ImageIcon,
  TrendingUp, Loader2, Lightbulb, Tag, Wand2, Zap
} from 'lucide-react';
import { format } from 'date-fns';

const fmtDate = (d) => {
  if (!d) return '—';
  if (typeof d === 'number') return format(new Date(d * 1000), 'MMM d, yyyy');
  const s = String(d).slice(0, 10);
  try { return format(new Date(s), 'MMM d, yyyy'); } catch { return s; }
};

const MetaField = ({ label, value, accent }) => (
  <div>
    <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">{label}</p>
    <p className={`text-sm ${accent || 'text-slate-700'} break-words`}>{value || '—'}</p>
  </div>
);

const isValid = (v) => v && v !== 'Not visible' && v !== 'None' && v !== 'N/A';

export default function AdDetailModal({ ad, open, onOpenChange, onAnalyze, analyzing, onSaveLearning, onRephraseLearning }) {
  const [learningDraft, setLearningDraft] = useState('');
  const [savingLearning, setSavingLearning] = useState(false);
  const [rephrasing, setRephrasing] = useState(false);

  // Reset draft whenever the viewed ad changes
  useEffect(() => {
    setLearningDraft(ad?.learning || '');
  }, [ad?.id, ad?.learning]);

  if (!ad) return null;

  const img = ad.image_urls?.[0];
  const vid = ad.video_urls?.[0];
  const a = ad.analysis || null;

  const handleSaveLearning = async () => {
    if (!onSaveLearning) return;
    // Skip if nothing changed (prevents unnecessary saves on blur)
    if ((learningDraft || '') === (ad.learning || '')) return;
    setSavingLearning(true);
    try {
      await onSaveLearning(ad.id, learningDraft);
    } finally {
      setSavingLearning(false);
    }
  };

  const handleRephrase = async () => {
    if (!onRephraseLearning) return;
    // If user typed fresh text that isn't saved yet, save first so backend has latest
    if ((learningDraft || '') !== (ad.learning || '')) {
      await handleSaveLearning();
    }
    setRephrasing(true);
    try {
      await onRephraseLearning(ad.id);
    } finally {
      setRephrasing(false);
    }
  };

  const rawHasContent = (ad.learning || '').trim().length > 3;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl h-[92vh] overflow-hidden p-0 gap-0 flex flex-col" data-testid="ad-detail-modal">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-white shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#2EB4AC]/15 to-cyan-100 flex items-center justify-center">
              <Radar className="h-4 w-4 text-[#2EB4AC]" />
            </div>
            <div>
              <DialogTitle className="text-base font-semibold text-slate-900 flex items-center gap-2">
                {ad.page_name || ad.source_brand}
                <Badge className="bg-slate-100 text-slate-600 border-slate-200 text-[10px] font-medium">Competitor</Badge>
                {ad.brand_assigned && (
                  <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-[10px] font-medium">
                    <Tag className="h-2.5 w-2.5 mr-1" />{ad.brand_assigned}
                  </Badge>
                )}
              </DialogTitle>
              <p className="text-[11px] text-slate-500">Meta Ad Library · {ad.country || 'IN'}</p>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] gap-0 flex-1 min-h-0">
          {/* LEFT: Media + Learning (own scroll) */}
          <div className="p-6 bg-slate-50/40 border-r border-slate-200 space-y-6 overflow-y-auto">
            <div className="rounded-xl overflow-hidden border border-slate-200 bg-white shadow-sm">
              {vid ? (
                <video src={vid} controls className="w-full bg-black aspect-[4/5] object-contain" />
              ) : img ? (
                <img alt="" src={img} referrerPolicy="no-referrer" className="w-full object-cover" />
              ) : (
                <div className="aspect-[4/5] flex items-center justify-center"><ImageIcon className="h-10 w-10 text-slate-300" /></div>
              )}
            </div>

            {ad.cta_link && (
              <a href={ad.cta_link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs font-medium text-[#2EB4AC] hover:underline">
                <ExternalLink className="h-3.5 w-3.5" /> Open landing page
              </a>
            )}

            {/* === LEARNING / INSIGHT — auto-saves on blur === */}
            <section className="rounded-2xl border border-amber-200 bg-amber-50/40 p-4" data-testid="learning-insight-block">
              <div className="flex items-center justify-between gap-2 mb-3">
                <div className="flex items-center gap-2">
                  <Lightbulb className="h-4 w-4 text-amber-500" strokeWidth={2} />
                  <p className="text-xs font-bold text-amber-600 tracking-[0.15em] uppercase">Learning / Insight</p>
                </div>
                {onRephraseLearning && (
                  <button
                    type="button"
                    onClick={handleRephrase}
                    disabled={rephrasing || savingLearning || !rawHasContent}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white text-[11px] font-semibold hover:from-violet-600 hover:to-fuchsia-600 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm"
                    data-testid="rephrase-learning-btn"
                    title={!rawHasContent ? 'Write a few words first' : 'Turn raw notes into actionable bullets'}
                  >
                    {rephrasing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Wand2 className="h-3 w-3" />}
                    {rephrasing ? 'Rephrasing…' : 'Rephrase'}
                  </button>
                )}
              </div>
              <textarea
                value={learningDraft}
                onChange={(e) => setLearningDraft(e.target.value)}
                onBlur={handleSaveLearning}
                placeholder={`- Brand Logo is clearly visible\n- tag line is Bold\n- Battery icons may visually represent low vs high energy after using creatine.`}
                className="w-full min-h-[160px] text-sm px-3 py-2.5 rounded-xl border border-amber-200 bg-white focus:outline-none focus:ring-2 focus:ring-amber-400/30 focus:border-amber-400 resize-y leading-relaxed"
                data-testid="competitor-learning-input"
              />
              <p className="text-[11px] text-amber-600/80 mt-2 italic">
                {savingLearning ? 'Saving…' : 'Saves automatically when you click outside'}
              </p>
            </section>
          </div>

          {/* RIGHT: Info + Insights (scrollable) + Actionable Summary (pinned bottom) */}
          <div className="flex flex-col min-h-0 p-6 gap-6">
            {/* Bordered scrollable card containing Primary text + Meta + Insights */}
            <div className="flex-1 min-h-0 rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden flex flex-col">
              <div className="px-5 py-3 border-b border-slate-100 bg-slate-50/60 flex items-center gap-2 shrink-0">
                <Sparkles className="h-3.5 w-3.5 text-slate-500" />
                <p className="text-[10px] font-bold text-slate-500 tracking-[0.15em] uppercase">Ad Details &amp; Insights</p>
              </div>
              <div className="p-5 space-y-8 overflow-y-auto flex-1 min-h-0">
              {/* Primary text + CTA */}
              <section className="space-y-3">
                <MetaField label="Primary text" value={ad.body_text ? <span className="whitespace-pre-wrap">{ad.body_text}</span> : null} />
                {ad.cta_text && (
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-emerald-50 border border-emerald-200">
                    <Target className="h-3.5 w-3.5 text-emerald-600" />
                    <span className="text-xs font-semibold text-emerald-700">{ad.cta_text}</span>
                  </div>
                )}
              </section>

              {/* Meta data grid */}
              <section className="grid grid-cols-2 gap-x-6 gap-y-5 pb-6 border-b border-slate-200">
                <MetaField label="Started" value={fmtDate(ad.started_on)} />
                <MetaField
                  label="Status"
                  value={(() => {
                    // Default to "Still running" — Meta's stored end date often becomes
                    // stale after scrape (advertisers extend silently). Only trust the
                    // date when `is_active === false` is explicitly set.
                    if (ad.is_active === false && ad.ended_on) return `Ended ${fmtDate(ad.ended_on)}`;
                    if (ad.is_active === false) return 'Ended';
                    return 'Still running';
                  })()}
                />
                <MetaField label="Platforms" value={(ad.platforms || []).join(' · ') || '—'} />
                <MetaField label="Format" value={(ad.display_format || '').replace('_', ' ') || '—'} />
                {ad.impressions_max && (
                  <div className="col-span-2">
                    <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">Impressions</p>
                    <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-blue-50 border border-blue-200">
                      <TrendingUp className="h-3.5 w-3.5 text-blue-600" />
                      <span className="text-sm font-semibold text-blue-700">
                        {ad.impressions_min ? `${Number(ad.impressions_min).toLocaleString()} – ${Number(ad.impressions_max).toLocaleString()}` : `≤ ${Number(ad.impressions_max).toLocaleString()}`}
                      </span>
                    </div>
                  </div>
                )}
              </section>

              {/* GPT-4o Insights */}
              {a ? (
                <section className="space-y-6">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center">
                      <Sparkles className="h-4 w-4 text-white" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-900">GPT-4o Insights</p>
                      <p className="text-[10px] text-slate-400">AI-generated from the creative</p>
                    </div>
                  </div>

                  {/* Classification */}
                  <div>
                    <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-2">Classification</p>
                    <div className="flex flex-wrap gap-1.5">
                      {ad.source_brand && <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 font-normal">{ad.source_brand}</Badge>}
                      {a.theme && <Badge className="bg-violet-100 text-violet-700 hover:bg-violet-100 font-normal">{a.theme}</Badge>}
                      {a.style && <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100 font-normal">{a.style}</Badge>}
                      {a.emotion && <Badge className="bg-rose-100 text-rose-700 hover:bg-rose-100 font-normal">{a.emotion}</Badge>}
                      {a.ad_format && <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 font-normal">{a.ad_format}</Badge>}
                    </div>
                  </div>

                  {/* Text Elements */}
                  {(isValid(a.headline) || isValid(a.sub_text) || isValid(a.cta) || isValid(a.price_text)) && (
                    <div>
                      <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-2">Text Elements</p>
                      <div className="space-y-3">
                        {isValid(a.headline) && (
                          <div className="bg-slate-50 rounded-lg p-3 border border-slate-100">
                            <p className="text-[10px] font-semibold text-slate-400 uppercase mb-1">Headline</p>
                            <p className="text-sm font-medium text-slate-900">{a.headline}</p>
                          </div>
                        )}
                        {isValid(a.sub_text) && (
                          <div className="bg-slate-50 rounded-lg p-3 border border-slate-100">
                            <p className="text-[10px] font-semibold text-slate-400 uppercase mb-1">Sub Text</p>
                            <p className="text-sm text-slate-700">{a.sub_text}</p>
                          </div>
                        )}
                        {isValid(a.cta) && (
                          <div className="bg-emerald-50 rounded-lg p-3 border border-emerald-200">
                            <p className="text-[10px] font-semibold text-emerald-600 uppercase mb-1">Call to Action</p>
                            <p className="text-sm font-semibold text-emerald-700">{a.cta}</p>
                          </div>
                        )}
                        {isValid(a.price_text) && (
                          <div className="bg-amber-50 rounded-lg p-3 border border-amber-200">
                            <p className="text-[10px] font-semibold text-amber-600 uppercase mb-1">Price</p>
                            <p className="text-sm font-bold text-amber-700">{a.price_text}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Visual Elements */}
                  {(isValid(a.people) || isValid(a.product_description) || isValid(a.background) || (a.dominant_colors?.length > 0)) && (
                    <div>
                      <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-2">Visual Elements</p>
                      <div className="grid grid-cols-2 gap-3">
                        {isValid(a.people) && (
                          <div className="bg-slate-50 rounded-lg p-3 border border-slate-100">
                            <p className="text-[10px] font-semibold text-slate-400 uppercase mb-1">People</p>
                            <p className="text-sm text-slate-700">{a.people}</p>
                          </div>
                        )}
                        {isValid(a.product_description) && (
                          <div className="bg-slate-50 rounded-lg p-3 border border-slate-100">
                            <p className="text-[10px] font-semibold text-slate-400 uppercase mb-1">Product</p>
                            <p className="text-sm text-slate-700">{a.product_description}</p>
                          </div>
                        )}
                        {isValid(a.background) && (
                          <div className="bg-slate-50 rounded-lg p-3 border border-slate-100">
                            <p className="text-[10px] font-semibold text-slate-400 uppercase mb-1">Background</p>
                            <p className="text-sm text-slate-700">{a.background}</p>
                          </div>
                        )}
                        {a.dominant_colors?.length > 0 && (
                          <div className="bg-slate-50 rounded-lg p-3 border border-slate-100">
                            <p className="text-[10px] font-semibold text-slate-400 uppercase mb-1">Colors</p>
                            <p className="text-sm text-slate-700">{a.dominant_colors.join(', ')}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Quick indicators */}
                  <div className="flex flex-wrap gap-1.5 pt-3 border-t border-slate-100">
                    {a.price_visible === 'Yes' && <span className="px-2.5 py-1 rounded-md bg-emerald-100 text-emerald-700 text-[11px] font-medium">Price Shown</span>}
                    {a.product_present === 'Yes' && <span className="px-2.5 py-1 rounded-md bg-blue-100 text-blue-700 text-[11px] font-medium">Product Visible</span>}
                    {isValid(a.people) && <span className="px-2.5 py-1 rounded-md bg-orange-100 text-orange-700 text-[11px] font-medium">Has People</span>}
                    {isValid(a.cta) && <span className="px-2.5 py-1 rounded-md bg-green-100 text-green-700 text-[11px] font-medium">Has CTA</span>}
                    {isValid(a.activity) && <span className="px-2.5 py-1 rounded-md bg-violet-100 text-violet-700 text-[11px] font-medium">{a.activity}</span>}
                  </div>
                </section>
              ) : (
                <Button
                  onClick={() => onAnalyze && onAnalyze(ad)}
                  disabled={analyzing || !img || !onAnalyze}
                  className="w-full bg-gradient-to-r from-violet-500 to-fuchsia-500 hover:from-violet-600 hover:to-fuchsia-600 text-white h-11"
                  data-testid="detail-analyze-btn"
                >
                  {analyzing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
                  {img ? 'Analyze with GPT-4o' : 'Video analysis not supported yet'}
                </Button>
              )}
              </div>
            </div>

            {/* === ACTIONABLE SUMMARY — pinned at the bottom of right column === */}
            {ad.learning_rephrased && (
              <section
                className="shrink-0 rounded-2xl border border-violet-200 bg-gradient-to-br from-violet-50 via-fuchsia-50/60 to-white p-4 max-h-[40%] overflow-y-auto"
                data-testid="actionable-summary-block"
              >
                <div className="flex items-center justify-between gap-2 mb-3 sticky top-0 bg-gradient-to-b from-violet-50 to-violet-50/95 -mx-4 -mt-4 px-4 pt-4 pb-2 rounded-t-2xl">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center">
                      <Zap className="h-3.5 w-3.5 text-white" strokeWidth={2.5} />
                    </div>
                    <p className="text-xs font-bold text-violet-700 tracking-[0.15em] uppercase">Actionable Summary</p>
                  </div>
                  {onRephraseLearning && (
                    <button
                      type="button"
                      onClick={handleRephrase}
                      disabled={rephrasing || !rawHasContent}
                      className="inline-flex items-center gap-1 text-[10px] font-semibold text-violet-600 hover:text-violet-800 disabled:opacity-40 uppercase tracking-wider"
                      data-testid="rephrase-learning-regen"
                    >
                      {rephrasing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Wand2 className="h-3 w-3" />}
                      Regenerate
                    </button>
                  )}
                </div>
                <div className="rounded-xl bg-white border border-violet-100 p-3.5 text-sm text-slate-700 whitespace-pre-wrap leading-relaxed" data-testid="actionable-summary-text">
                  {ad.learning_rephrased}
                </div>
                {ad.learning_rephrased_at && (
                  <p className="text-[10px] text-violet-500/80 mt-2 italic">
                    Generated {fmtDate(ad.learning_rephrased_at)} · AI-refined from your raw notes
                  </p>
                )}
              </section>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
